import React, { useEffect, useMemo, useState } from 'react';
import { useQuery } from 'react-query';
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  MenuItem,
  Typography,
} from '@mui/material';
import { teacherModuleAPI } from '../../services/api';
import { DAY_CHECKIN_STATUS_FILTERS, dayCheckinStatusColor } from '../../utils/dayCheckinStatus';

function localToday() {
  const now = new Date();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${now.getFullYear()}-${month}-${day}`;
}

function monthEnd(month) {
  const [year, monthNum] = month.split('-').map(Number);
  return new Date(Date.UTC(year, monthNum, 0)).toISOString().slice(0, 10);
}

function dateForMonth(month, today = localToday()) {
  if (today.slice(0, 7) === month) return today;
  return `${month}-01`;
}

export default function TeacherDayStartTab() {
  const today = localToday();
  const [month, setMonth] = useState(today.slice(0, 7));
  const [date, setDate] = useState(today);
  const [status, setStatus] = useState('all');
  const [teacherSearch, setTeacherSearch] = useState('');
  const [supervisorId, setSupervisorId] = useState('');

  useEffect(() => {
    setDate(dateForMonth(month));
  }, [month]);

  const query = useQuery(
    ['teacherDayCheckinTeam', date],
    async () => {
      const res = await teacherModuleAPI.getDayCheckinTeam({ date });
      return res.data?.data;
    },
    { enabled: !!date, keepPreviousData: true }
  );

  const data = query.data;
  const searched = useMemo(() => {
    const q = teacherSearch.trim().toLowerCase();
    return (data?.rows || []).filter((row) => {
      if (supervisorId === '__none__' && row.supervisorTeacherId) return false;
      if (supervisorId && supervisorId !== '__none__' && row.supervisorTeacherId !== supervisorId) return false;
      if (q && !String(row.teacherName || '').toLowerCase().includes(q)) return false;
      return true;
    });
  }, [data, supervisorId, teacherSearch]);

  const counts = useMemo(() => {
    const tally = {};
    for (const row of searched) tally[row.status] = (tally[row.status] || 0) + 1;
    return tally;
  }, [searched]);

  const rows = status === 'all' ? searched : searched.filter((row) => row.status === status);
  const maxDate = monthEnd(month);

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Times are in each teacher&apos;s work-hours timezone. On time means marked within 5 minutes after the
        expected start. A supervisor sees only their own team.
      </Typography>
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mb: 2 }}>
        <TextField
          label="Month"
          type="month"
          size="small"
          value={month}
          onChange={(e) => setMonth(e.target.value)}
          InputLabelProps={{ shrink: true }}
        />
        <TextField
          label="Day"
          type="date"
          size="small"
          value={date}
          onChange={(e) => setDate(e.target.value)}
          inputProps={{ min: `${month}-01`, max: maxDate }}
          InputLabelProps={{ shrink: true }}
        />
        <TextField
          label="Teacher"
          size="small"
          value={teacherSearch}
          onChange={(e) => setTeacherSearch(e.target.value)}
          placeholder="Search name"
        />
        {data?.canFilterSupervisor ? (
          <TextField
            select
            label="Supervisor"
            size="small"
            value={supervisorId}
            onChange={(e) => setSupervisorId(e.target.value)}
            sx={{ minWidth: 220 }}
          >
            <MenuItem value="">All supervisors</MenuItem>
            <MenuItem value="__none__">No supervisor</MenuItem>
            {(data.supervisors || []).map((supervisor) => (
              <MenuItem key={supervisor.id} value={supervisor.id}>
                {supervisor.name}
              </MenuItem>
            ))}
          </TextField>
        ) : null}
      </Stack>

      <Stack direction="row" flexWrap="wrap" gap={1} sx={{ mb: 2 }}>
        {DAY_CHECKIN_STATUS_FILTERS.map((item) => {
          const count = item.id === 'all' ? searched.length : counts[item.id] || 0;
          return (
            <Chip
              key={item.id}
              label={`${item.label} (${count})`}
              color={item.id === 'all' ? 'primary' : dayCheckinStatusColor(item.id)}
              variant={status === item.id ? 'filled' : 'outlined'}
              onClick={() => setStatus(item.id)}
            />
          );
        })}
      </Stack>

      {query.isLoading ? (
        <CircularProgress size={24} />
      ) : query.isError ? (
        <Alert severity="error">
          {query.error?.response?.data?.message || 'Could not load day start'}
        </Alert>
      ) : data?.emptyReason === 'no_team' ? (
        <Alert severity="info">No teachers are assigned to you yet.</Alert>
      ) : rows.length === 0 ? (
        <Typography color="text.secondary">No teachers match these filters for this day.</Typography>
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Teacher</TableCell>
                {data?.canFilterSupervisor ? <TableCell>Supervisor</TableCell> : null}
                <TableCell>Expected start</TableCell>
                <TableCell>Marked at</TableCell>
                <TableCell>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row) => (
                <TableRow key={row.teacherId} hover>
                  <TableCell sx={{ fontWeight: 600 }}>{row.teacherName || '—'}</TableCell>
                  {data?.canFilterSupervisor ? <TableCell>{row.supervisorName || '—'}</TableCell> : null}
                  <TableCell>{row.expectedStartLabel || '—'}</TableCell>
                  <TableCell>{row.markedAtLabel || '—'}</TableCell>
                  <TableCell>
                    <Chip size="small" label={row.statusLabel} color={dayCheckinStatusColor(row.status)} />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
}
